import DesignTokens from '@tableau/design-tokens';
import { TabStyles } from '@tableau/widgets';

import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { applyColor }from '~src/style/ColorService';

import { cssClass } from '../../style';


const cssPrefix = 'site-picker';

export namespace SitePickerStyles {
  const SITE_PICKER_BUTTON_FONT_SIZE = '14px';
  const SITE_PICKER_FONT_SIZE = `${TabStyles.Typography.Sizing.Default}px`;
  const CHECK_SIZE = 10;
  const ITEM_HEIGHT_PX = `${CommonStyles.Sizes.DropdownItemHeight}px`;
  const FONT_COLOR = CommonStyles.Colors.Grey51;

  const SitePicker: React.CSSProperties = {
    marginBottom: '40px',
    fontSize: SITE_PICKER_FONT_SIZE,
  };

  export const SitePickerExpanded: React.CSSProperties = {
    ...SitePicker,
    maxWidth: '165px',
    marginRight: '37px',
    marginLeft: '37px',
  };

  export const SitePickerMinimized: React.CSSProperties = {
    ...SitePicker,
    minWidth: '44px',
    marginRight: '4px',
    marginLeft: '17px',
  };

  export const ItemContainer = cssClass(cssPrefix, 'item-container', {
    display: 'flex',
    flexGrow: 1,
    alignItems: 'center',
    flexDirection: 'row',
    color: applyColor(DesignTokens.color.font.default.default, TabStyles!.Typography!.Color!.Primary),
    justifyContent: 'space-between',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    textDecoration: 'none',
    height: ITEM_HEIGHT_PX,
    $nest: {
      '&:hover': {
        textDecoration: 'none'
      }
    }
  });

  export const SitePickerButton = cssClass(cssPrefix, 'button', {
    ...CommonStyles.Layout.TextEllipsis,
    fontWeight: 'normal',
    fontSize: SITE_PICKER_BUTTON_FONT_SIZE,
    color: applyColor(DesignTokens.color.font.default.default, FONT_COLOR)
  });

  const SitePickerItemStyles: React.CSSProperties = {
    ...CommonStyles.Layout.TextEllipsis,
    width: '100%',
    verticalAlign: 'middle',
    fontSize: SITE_PICKER_FONT_SIZE,
    paddingLeft: 8,
  };

  const ItemStyles: React.CSSProperties = {
    fontWeight: 'normal',
    ...CommonStyles.Layout.TextEllipsis,
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'row',
    cursor: 'pointer',
    fontSize: SITE_PICKER_FONT_SIZE,
    color: applyColor(DesignTokens.color.font.default.default, FONT_COLOR),
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  };

  export const Item = cssClass(cssPrefix, 'item', {
    ...ItemStyles,
  });

  const TenantItemStyles: React.CSSProperties = {
    fontWeight: 500,
    ...CommonStyles.Layout.TextEllipsis,
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'row',
    fontSize: SITE_PICKER_FONT_SIZE,
    color: applyColor(DesignTokens.color.font.default.subtle, '#939393'),
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    fontFamily: 'BentonSans-Medium'
  };

  export const TenantCloudManagerItem = cssClass(cssPrefix, 'tenant-item', SitePickerItemStyles, {
    fontWeight: 500,
    fontFamily: 'BentonSans-Medium',
    color: applyColor(DesignTokens.color.font.default.default, FONT_COLOR)
  });

  export const TenantItem = cssClass(cssPrefix, 'tenant-item', {
    ...TenantItemStyles,
  });

  export const LinkEnabledSiteItem = cssClass(cssPrefix, 'link-enabled-site-item', SitePickerItemStyles, {
    fontWeight: 350,
    fontFamily: 'BentonSans-Book',
    color: applyColor(DesignTokens.color.font.default.default, FONT_COLOR)
  });

  export const SelectedItem = cssClass(cssPrefix, 'selected-item', {
    ...ItemStyles,
    marginLeft: 0,
  });

  export const CheckImgForSiteItem = cssClass(cssPrefix, 'check-img', {
    marginRight: TabStyles!.Spacing!.DefaultSpace,
    minHeight: CHECK_SIZE,  // need to use min because Chrome for some reason makes the image smaller
    minWidth: CHECK_SIZE    // when using exact height and width
  });

  const listWidth = 188;
  const panelHMargins = CommonStyles.Sizes.DefaultSpace;
  const panelWidth = listWidth + (2 * panelHMargins);  // total 200
  const listHeight = 280;
  export const List: React.CSSProperties = {
    maxHeight: listHeight,
    paddingLeft: '0px',
    paddingRight: '0px',
  };

  export const Panel = cssClass(cssPrefix, 'panel', {
    paddingTop: `${CommonStyles.Sizes.DefaultSpace}px`,
    paddingBottom: `${CommonStyles.Sizes.DefaultSpace}px`,
    paddingLeft: `${panelHMargins}px`,
    paddingRight: `${panelHMargins}px`,
    width: panelWidth,
    minWidth: panelWidth
  });

  export const TenantEnabledPanel = cssClass(cssPrefix, 'panel', {
    paddingTop: `${CommonStyles.Sizes.DefaultSpace}px`,
    paddingBottom: `${CommonStyles.Sizes.DefaultSpace}px`,
    paddingLeft: `${panelHMargins}px`,
    paddingRight: `${panelHMargins}px`,
    width: panelWidth,
    minWidth: panelWidth,
    $nest: {
      '[role=option]': {
        paddingLeft: 0
      }
    }
  });

  export const Spinner = cssClass(cssPrefix, 'spinner', {
    marginTop: '48px',
    marginBottom: '48px',
    width: `${panelWidth}px`,
    textAlign: 'center'
  });

  export const CheckImgForAllSitesItem = cssClass(cssPrefix, 'check-img-all-sites', {
    marginLeft: 12,
    marginRight: TabStyles!.Spacing!.DefaultSpace,
    minHeight: CHECK_SIZE,  // need to use min because Chrome for some reason makes the image smaller
    minWidth: CHECK_SIZE    // when using exact height and width
  });

  const manageAllSitesMargin = CommonStyles.Sizes.DefaultSpace;

  export const ManageAllSiteStyle: React.CSSProperties = {
    fontWeight: 'bold',
    color: applyColor(DesignTokens.color.link.standalone.default.default, CommonStyles.Colors.HyperlinkBlue),
    fontSize: SITE_PICKER_FONT_SIZE,
    lineHeight: ITEM_HEIGHT_PX,
    marginLeft: manageAllSitesMargin + CHECK_SIZE + (2 * TabStyles.Spacing.DefaultSpace),
    marginRight: `${manageAllSitesMargin}px`,
    marginTop: `${manageAllSitesMargin}px`,
    marginBottom: `${manageAllSitesMargin}px`
  };

  export const ManageAllSitesLink = cssClass(cssPrefix, 'manage-all-sites-link', ManageAllSiteStyle);

  export const ManageAllSitesLinkSelected = cssClass(cssPrefix, 'manage-all-sites-link-selected', {
    ...ManageAllSiteStyle,
    marginLeft: 0
  });
}
